import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, UserPlus, Send, PackageCheck, Wallet, BarChart3, Settings,
  Truck, Moon, Sun, ChevronDown, Bell, Building2, ArrowLeftRight, LogOut, User,
  PanelLeftClose, PanelLeftOpen, MapPin, ShieldCheck,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/onboarding", label: "Onboarding", icon: UserPlus, id: "onboarding" },
  { to: "/dispatch", label: "Dispatch", icon: Send, id: "dispatch" },
  { to: "/receive", label: "Receive", icon: PackageCheck, id: "receive" },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck, id: "compliance" },
  { to: "/accounting", label: "Accounting", icon: Wallet, id: "accounting" },
  { to: "/reports", label: "Reports", icon: BarChart3, id: "reports" },
  { to: "/settings", label: "Settings", icon: Settings, id: "settings" },
];

export const Layout = ({ children }) => {
  const { hubs, selectedHub, setSelectedHub, theme, setTheme, year, setYear,
    company, companies, setCompany, clearCompany } = useApp();
  const { user, modules, logout, isAdmin } = useAuth();
  const loc = useLocation();
  const [collapsed, setCollapsed] = React.useState(localStorage.getItem("tl_sidebar_collapsed") === "1");
  const toggleCollapsed = () => setCollapsed((c) => { localStorage.setItem("tl_sidebar_collapsed", c ? "0" : "1"); return !c; });
  const visibleNav = nav.filter((n) => modules.includes(n.id));
  const current = nav.find((n) => n.to === loc.pathname) || nav[0];
  const activeHubName = hubs.find((h) => h.id === selectedHub)?.name || "Select hub";
  const initials = (company?.name || "T").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const [flash, setFlash] = React.useState(null);
  const prevSwitchRef = React.useRef({ hub: selectedHub, comp: company?.id });
  const lastCompanySwitchRef = React.useRef(0);
  const flashTimerRef = React.useRef(null);
  React.useEffect(() => {
    const prev = prevSwitchRef.current;
    prevSwitchRef.current = { hub: selectedHub, comp: company?.id };
    let next = null;
    if (prev.comp !== company?.id) {
      if (company?.id) {
        lastCompanySwitchRef.current = Date.now();
        next = { kind: "company", name: company?.name || "" };
      }
    } else if (prev.hub !== selectedHub) {
      if (selectedHub && Date.now() - lastCompanySwitchRef.current > 2000) {
        next = { kind: "hub", name: activeHubName };
      }
    }
    if (!next) return;
    setFlash(next);
    clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlash(null), 1600);
  }, [selectedHub, activeHubName, company?.id, company?.name]);
  React.useEffect(() => () => clearTimeout(flashTimerRef.current), []);

  const navigate = useNavigate();
  const [compAlerts, setCompAlerts] = React.useState(0);
  React.useEffect(() => {
    if (!selectedHub || selectedHub === "all" || !modules.includes("compliance")) { setCompAlerts(0); return; }
    api.get("/compliance/summary", { params: { hub: selectedHub } }).then((r) => setCompAlerts(r.data.alerts || 0)).catch(() => setCompAlerts(0));
  }, [selectedHub, company?.id]);

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <aside data-testid="sidebar" className={`${collapsed ? "w-[78px]" : "w-64"} shrink-0 hidden md:flex flex-col fixed inset-y-0 left-0 z-30 border-r border-[hsl(var(--sidebar-border))] bg-gradient-to-b from-[hsl(214_50%_15%)] via-[hsl(219_52%_10%)] to-[hsl(222_56%_7%)] transition-[width] duration-300 ease-in-out`}>
        <button
          data-testid="sidebar-collapse-toggle"
          onClick={toggleCollapsed}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="absolute top-[74px] -right-3.5 z-40 h-7 w-7 rounded-full border border-border bg-card shadow-md inline-flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
        >
          {collapsed ? <PanelLeftOpen className="h-3.5 w-3.5" /> : <PanelLeftClose className="h-3.5 w-3.5" />}
        </button>
        <div className={`h-16 flex items-center ${collapsed ? "justify-center px-2" : "gap-2.5 px-5"} border-b border-[hsl(var(--sidebar-border))]`}>
          <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center overflow-hidden p-0.5 shadow-lg shadow-primary/20 shrink-0">
            <img src={company?.logo || "/logo.png"} alt={company?.name || "Company"} className="max-h-full max-w-full object-contain" />
          </div>
          {!collapsed && (
            <div className="font-display font-bold tracking-tight text-base text-white truncate">
              {company?.name || "Accounting"}
            </div>
          )}
        </div>

        {/* Company badge */}
        <div className={`px-3 pt-3 ${collapsed ? "flex justify-center" : ""}`}>
          {isAdmin ? (
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="company-switcher" title={collapsed ? (company?.name || "Company") : undefined} className={`${collapsed ? "" : "w-full"} flex items-center gap-2.5 ${collapsed ? "justify-center px-2" : "px-3"} py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-primary/40 transition-colors text-left group`}>
              <div className="h-8 w-8 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center text-primary text-xs font-bold shrink-0">{initials}</div>
              {!collapsed && (
                <>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">Company</div>
                    <div className="text-sm font-semibold text-white truncate">{company?.name || "—"}</div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" />
                </>
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Switch company</DropdownMenuLabel>
              {companies.map((c) => (
                <DropdownMenuItem key={c.id} data-testid={`switch-company-${c.id}`} onClick={() => setCompany(c.id)}>
                  <Building2 className="h-4 w-4 mr-2 opacity-70" />{c.name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="manage-companies" onClick={clearCompany}>
                <ArrowLeftRight className="h-4 w-4 mr-2 opacity-70" />All companies
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          ) : (
            <div className={`${collapsed ? "" : "w-full"} flex items-center gap-2.5 ${collapsed ? "justify-center px-2" : "px-3"} py-2.5 rounded-xl bg-white/5 border border-white/10`} data-testid="company-badge" title={collapsed ? (company?.name || "Company") : undefined}>
              <div className="h-8 w-8 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center text-primary text-xs font-bold shrink-0">{initials}</div>
              {!collapsed && (
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">Company</div>
                  <div className="text-sm font-semibold text-white truncate">{company?.name || "—"}</div>
                </div>
              )}
            </div>
          )}
        </div>

        <nav className={`flex-1 ${collapsed ? "p-2" : "p-3"} space-y-1`}>
          {visibleNav.map((n) => (
            <NavLink
              key={n.id}
              to={n.to}
              data-testid={`sidebar-nav-${n.id}`}
              title={collapsed ? n.label : undefined}
              className={({ isActive }) =>
                `flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3.5"} py-2.5 rounded-lg text-sm font-medium transition-all group relative ${
                  isActive
                    ? "bg-gradient-to-r from-primary to-[hsl(32_95%_55%)] text-white shadow-lg shadow-primary/25"
                    : "text-slate-300/80 hover:text-white hover:bg-white/[0.06]"
                }`
              }
            >
              <n.icon className="h-[18px] w-[18px] shrink-0" />
              {!collapsed && n.label}
            </NavLink>
          ))}
        </nav>
        <div className={`${collapsed ? "p-2 text-center" : "p-4"} border-t border-[hsl(var(--sidebar-border))] text-xs text-slate-500`}>
          <div className="font-mono">{collapsed ? "v2.0" : "v2.0 · Accounting Console"}</div>
          {!collapsed && <div className="mt-1 truncate">{company?.name}</div>}
        </div>
      </aside>

      {/* Main */}
      <div className={`flex-1 ${collapsed ? "md:ml-[78px]" : "md:ml-64"} min-w-0 transition-[margin] duration-300 ease-in-out`}>
        <header className="h-16 sticky top-0 z-20 flex items-center justify-between gap-3 px-4 sm:px-6 lg:px-8 border-b border-border bg-background/70 backdrop-blur-xl">
          <div>
            <h1 className="font-display font-bold text-lg sm:text-xl tracking-tight" data-testid="page-title">
              {current.label}
            </h1>
            <p className="text-xs text-muted-foreground hidden sm:block">{company?.name} · FY {year}</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="year-select"
                className="h-9 px-3 rounded-lg border border-border bg-card text-sm font-medium inline-flex items-center gap-1.5 hover:bg-accent transition-colors"
              >
                <span className="font-mono">{year}</span>
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {[2026, 2025, 2024].map((y) => (
                  <DropdownMenuItem key={y} data-testid={`year-option-${y}`} onClick={() => setYear(y)}>{y}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="hub-filter-select"
                className={`h-9 px-3 rounded-lg border bg-card text-sm font-medium inline-flex items-center gap-2 hover:bg-accent transition-all max-w-[180px] ${flash?.kind === "hub" ? "border-primary/60 ring-2 ring-primary/40 shadow-lg shadow-primary/20" : "border-border"}`}
              >
                <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                <span className="truncate">{activeHubName}</span>
                <ChevronDown className="h-3.5 w-3.5 opacity-60 shrink-0" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="hub-option-all" onClick={() => setSelectedHub("all")} className="hidden">All Locations</DropdownMenuItem>
                {hubs.map((h) => (
                  <DropdownMenuItem key={h.id} data-testid={`hub-option-${h.code}`} onClick={() => setSelectedHub(h.id)}>{h.name}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <button data-testid="notifications-button" onClick={() => navigate("/compliance")} title={compAlerts > 0 ? `${compAlerts} compliance alert(s) — open Compliance` : "Compliance alerts"} className="h-9 w-9 rounded-lg border border-border bg-card relative inline-flex items-center justify-center hover:bg-accent transition-colors">
              <Bell className="h-4 w-4" />
              {compAlerts > 0
                ? <span data-testid="compliance-alert-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold inline-flex items-center justify-center shadow-sm">{compAlerts}</span>
                : <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-rose-500" />}
            </button>

            <button
              data-testid="theme-toggle-button"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="h-9 w-9 rounded-lg border border-border bg-card inline-flex items-center justify-center hover:bg-accent transition-colors"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu" className="h-9 w-9 rounded-full bg-gradient-to-br from-primary to-cyan-500 hidden sm:flex items-center justify-center text-white text-xs font-bold hover:opacity-90 transition-opacity">
                {(user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                  <div className="mt-1 inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">{user?.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-rose-500 focus:text-rose-500">
                  <LogOut className="h-4 w-4 mr-2" />Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <motion.main
          key={loc.pathname + "|" + selectedHub}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="p-4 sm:p-6 lg:p-8 w-full"
        >
          {children}
        </motion.main>

        <AnimatePresence>
          {flash && (
            <motion.div
              key="switch-flash"
              data-testid="switch-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-md pointer-events-none"
            >
              <motion.div
                initial={{ scale: 0.8, y: 24, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.92, y: -14, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                className="relative overflow-hidden flex flex-col items-center gap-4 px-12 py-8 rounded-3xl border border-primary/25 bg-card shadow-2xl shadow-primary/25"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-cyan-500/10" />
                <div className="relative">
                  <motion.div
                    className="absolute inset-0 rounded-2xl bg-primary/50"
                    animate={{ scale: [1, 1.9], opacity: [0.5, 0] }}
                    transition={{ duration: 1.3, repeat: Infinity, ease: "easeOut" }}
                  />
                  <div className="relative h-14 w-14 rounded-2xl bg-gradient-to-br from-primary to-[hsl(32_95%_55%)] flex items-center justify-center text-white shadow-lg shadow-primary/40">
                    {flash.kind === "company" ? <Building2 className="h-7 w-7" /> : <MapPin className="h-7 w-7" />}
                  </div>
                </div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12, duration: 0.3 }}
                  className="relative text-center"
                >
                  <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground font-mono">
                    {flash.kind === "company" ? "Workspace switched" : "Hub switched"}
                  </div>
                  <div className="font-display font-bold text-2xl tracking-tight mt-1 max-w-[320px] truncate" data-testid="switch-overlay-name">
                    {flash.name}
                  </div>
                </motion.div>
                <motion.div
                  className="relative h-0.5 rounded-full bg-gradient-to-r from-transparent via-primary to-transparent"
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 180, opacity: 1 }}
                  transition={{ delay: 0.25, duration: 0.5, ease: "easeOut" }}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
